// ---------------------------------------------------------------------------
// EditorDndContext — wraps the editor with @dnd-kit DndContext.
//
// Handles block reorder within a page via drag and drop.
// Component-level DnD will be added when element-level editing is implemented.
// ---------------------------------------------------------------------------

import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useState, useCallback } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { GripVertical } from 'lucide-react'

/** Discriminated DnD source types */
export type DndItemType = 'block'

interface Props {
  children: React.ReactNode
}

export function EditorDndContext({ children }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeLabel, setActiveLabel] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
    setActiveLabel(
      (event.active.data.current?.label as string) ?? (event.active.id as string),
    )
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    setActiveLabel(null)

    if (!over || active.id === over.id) return

    // Block reorder within page — look up current indices from store
    // instead of using render-time data to avoid stale index issues.
    if (active.data.current?.type === 'block' && over.data.current?.type === 'block') {
      const pageId = active.data.current.pageId as string
      const state = useEditorStore.getState()
      const page = state.app?.pages.find((p) => p.id === pageId)
      if (page) {
        const fromIndex = page.blocks.indexOf(active.id as string)
        const toIndex = page.blocks.indexOf(over.id as string)
        if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
          state.reorderPageBlocks(pageId, fromIndex, toIndex)
        }
      }
    }
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {activeId && (
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-primary/50 bg-background px-4 py-3 text-sm shadow-lg">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            <span>{activeLabel ?? 'Block'}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
